import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import useOutsideClick from '../../hooks/useOutsideClick'
import {
  CloseButton,
  StyledActions,
  StyledBody,
  StyledButtonDownload,
  StyledCard,
  StyledCloseButtonDiv,
  StyledMedia,
  StyledMediaViewer,
  StyledModal,
  StyledSubtitle,
  StyledSubtitleContainer,
  StyledTitle,
} from './MediaCardViewer.styled'
import NormalizeMedia from '../../utils/NormalizeMedia'
import { getCloudinaryImageAttachment } from '../../utils/getCloudinaryImageAttachment'
import { getSizeFromDocument } from '../../utils/GetSizeFromDocument'
import MediaTypeEnum from '../../enums/MediaType'
import IconButton from '../IconButton/IconButton'
import pxToRem from '../../utils/pxToRem'
import ArrowPrev from '../../images/icons/arrow-left-white.svg'
import ArrowNext from '../../images/icons/arrow-right-white.svg'
import useMediaQuery from '../../hooks/useMediaQuery'
import { deviceMax } from '../../theme/breakpoints'

const BASE_URL = 'https://res.cloudinary.com/constellium/image/upload'
const BUTTON_DOWNLOAD_LABEL = 'Télécharger la photo'

const MediaCardViewer = (props) => {
  const { className, onClose, medias, activeId, onChange } = props

  const [currentCard, setCurrentCard] = useState(null)
  const [downloadUrl, setDownloadUrl] = useState(null)
  const [descriptionImage, setDescriptionImage] = useState(null)
  const [isVideo, setIsVideo] = useState(false)

  const isMobile = useMediaQuery(deviceMax.mobileL)
  const wrapperRef = useRef(null)
  useOutsideClick(wrapperRef, onClose)

  const togglePrev = () => {
    const cardIndex = medias.findIndex((card) => card.id === activeId)
    if (cardIndex > 0) {
      const nextCard = medias[cardIndex - 1]
      setCurrentCard(nextCard)
      onChange(nextCard.id)
    }
  }

  const toggleNext = () => {
    const cardIndex = medias.findIndex((card) => card.id === activeId)
    if (cardIndex < medias.length - 1) {
      const nextCard = medias[cardIndex + 1]
      setCurrentCard(nextCard)
      onChange(nextCard.id)
    }
  }

  useEffect(() => {
    const card = medias.find((card) => card.id === activeId)
    setCurrentCard(card)
    const isCloudinary =
      card.image?.cloudinaryAsset && card.image?.cloudinaryAsset.length > 0
    const isContentful =
      card.image?.contentfulAsset && card.image?.contentfulAsset.gatsbyImageData
    const isVideo =
      (card.image?.cloudinaryAsset &&
        card.image?.cloudinaryAsset[0]?.resource_type !==
          MediaTypeEnum.image) ||
      card.image?.youtubeUrl

    let currentDownloadUrl = null

    if (!isVideo) {
      currentDownloadUrl = isCloudinary
        ? getCloudinaryImageAttachment(
            BASE_URL,
            card.image.cloudinaryAsset[0].format,
            card.image.name,
            card.image.cloudinaryAsset[0].public_id
          )
        : isContentful
        ? card.image?.contentfulAsset.gatsbyImageData.images.fallback?.src
        : null

      setDescriptionImage(getSizeFromDocument(card.image, true))
    } else {
      setDescriptionImage(card?.descriptionHtml?.content)
    }

    setDownloadUrl(currentDownloadUrl)
    setIsVideo(isVideo)
  }, [activeId])

  let mediaCard = useMemo(() => {
    if (currentCard && isVideo) {
      return (
        <StyledCard>
          <StyledMedia>{NormalizeMedia(currentCard.image)}</StyledMedia>
          <StyledBody>
            <StyledSubtitleContainer>
              <StyledTitle>{currentCard.titleLevel1}</StyledTitle>
              {currentCard.titleLevel2 && (
                <StyledSubtitle>{currentCard.titleLevel2}</StyledSubtitle>
              )}
            </StyledSubtitleContainer>
          </StyledBody>
        </StyledCard>
      )
    }

    if (currentCard && !isVideo) {
      return (
        <StyledCard>
          <StyledMedia>{NormalizeMedia(currentCard.image)}</StyledMedia>
          <StyledBody>
            <StyledSubtitleContainer>
              <StyledTitle>{currentCard.titleLevel1}</StyledTitle>
              {currentCard.titleLevel2 && (
                <StyledSubtitle>{currentCard.titleLevel2}</StyledSubtitle>
              )}
            </StyledSubtitleContainer>
            <StyledButtonDownload
              urlDownload={downloadUrl}
              description={descriptionImage}
              hasDownloadIcon={true}
              isImage={true}
              name={BUTTON_DOWNLOAD_LABEL}
            />
          </StyledBody>
        </StyledCard>
      )
    }

    return null
  }, [currentCard, descriptionImage, downloadUrl, isVideo])

  return (
    <StyledModal className={className}>
      <StyledMediaViewer ref={wrapperRef}>
        <StyledCloseButtonDiv>
          <CloseButton onClick={onClose} />
        </StyledCloseButtonDiv>
        {!isMobile && (
          <IconButton
            onClick={togglePrev}
            height={pxToRem(40)}
            width={pxToRem(40)}
          >
            <ArrowPrev />
          </IconButton>
        )}
        {mediaCard}
        {isMobile && (
          <StyledActions>
            <IconButton
              onClick={togglePrev}
              height={pxToRem(40)}
              width={pxToRem(40)}
            >
              <ArrowPrev />
            </IconButton>
            <IconButton
              onClick={toggleNext}
              height={pxToRem(40)}
              width={pxToRem(40)}
            >
              <ArrowNext />
            </IconButton>
          </StyledActions>
        )}
        {!isMobile && (
          <IconButton
            onClick={toggleNext}
            height={pxToRem(40)}
            width={pxToRem(40)}
          >
            <ArrowNext />
          </IconButton>
        )}
      </StyledMediaViewer>
    </StyledModal>
  )
}

MediaCardViewer.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func.isRequired,
}

export default MediaCardViewer
